import React, { useCallback } from "react";
import PropTypes from "prop-types";
import Button from "../../atoms/Button";
import StructuredListRow from "../StructuredListRow";
import UserCardAvatar from "../UserCardAvatar";
import { pickFriendshipActionIcon } from "../../atoms/icons/FriendshipActionIcons";
import "./index.scss";

export default function FriendListRow({
  userId,
  username,
  displayName,
  identityMetaLines = [],
  relationshipLabel,
  metaItems = [],
  summary,
  snapshotLines = [],
  snapshotFallback,
  connectionHeading,
  connectionValue,
  previewAriaLabel,
  avatarAlt,
  avatarUrl,
  onPreview,
  actions = [],
  presenceStatus = null,
  publicMedia = false,
}) {
  const isClickable = typeof onPreview === "function";
  const resolvedDisplayName = displayName || username;

  const createActionHandler = useCallback((action) => () => {
    if (action.disabled || action.busy) {
      return;
    }
    action.onClick?.();
  }, []);

  const rowClassName = [
    "friend-list-row",
    isClickable ? "friend-list-row--interactive" : "",
  ].filter(Boolean).join(" ");

  const hasSnapshotContent = metaItems.length > 0 || Boolean(summary);
  const actionClassNames = {
    primary: "friend-list-row__button--primary",
    secondary: "friend-list-row__button--secondary",
    success: "friend-list-row__button--success",
    danger: "friend-list-row__button--danger",
  };

  return (
    <StructuredListRow
      className={rowClassName}
      onActivate={isClickable ? onPreview : undefined}
      activateAriaLabel={previewAriaLabel}
      identityClassName="friend-list-row__identity"
      identity={(
        <>
          <UserCardAvatar
            userId={userId}
            username={username}
            alt={avatarAlt}
            avatarUrl={avatarUrl}
            publicMedia={publicMedia}
            size="76px"
            radius="22px"
            status={presenceStatus || undefined}
            enableProfilePreview={false}
            className="friend-list-row__avatar"
            avatarClassName="friend-list-row__avatar-media"
          />
          <div className="friend-list-row__identity-copy">
            {relationshipLabel ? (
              <span className="friend-list-row__eyebrow">{relationshipLabel}</span>
            ) : null}
            <h4 className="friend-list-row__name">{resolvedDisplayName}</h4>
            {identityMetaLines.length > 0 ? (
              <div className="friend-list-row__identity-meta">
                {identityMetaLines.map((line, index) => (
                  <p key={`${line}-${index}`} className="friend-list-row__identity-meta-line">{line}</p>
                ))}
              </div>
            ) : null}
          </div>
        </>
      )}
      snapshotClassName="friend-list-row__snapshot"
      snapshot={(
        <>
          {metaItems.length > 0 ? (
            <div className="friend-list-row__chips">
              {metaItems.map((item) => (
                <span key={item} className="friend-list-row__chip">
                  {item}
                </span>
              ))}
            </div>
          ) : null}
          {summary ? (
            <p className="friend-list-row__summary">{summary}</p>
          ) : snapshotLines.length > 0 ? (
            <div className="friend-list-row__snapshot-lines">
              {snapshotLines.map((line, index) => (
                <p key={`${line}-${index}`} className="friend-list-row__snapshot-line">{line}</p>
              ))}
            </div>
          ) : !hasSnapshotContent && snapshotFallback ? (
            <p className="friend-list-row__summary friend-list-row__summary--muted">
              {snapshotFallback}
            </p>
          ) : null}
        </>
      )}
      connectionClassName="friend-list-row__connection"
      connection={(
        <>
          <span className="friend-list-row__connection-heading">{connectionHeading}</span>
          <span className="friend-list-row__connection-value">{connectionValue}</span>
        </>
      )}
      actionsClassName="friend-list-row__actions"
      actions={(
        <>
          {actions.map((action) => {
            const Icon = pickFriendshipActionIcon(action);
            const label = action.busy ? (action.busyLabel || action.label) : action.label;
            return (
              <Button
                key={action.key || action.label}
                variant={action.variant || "primary"}
                size="small"
                shape="pill"
                className={[
                  "friend-list-row__button",
                  actionClassNames[action.variant || "primary"] || "",
                ].filter(Boolean).join(" ")}
                onClick={createActionHandler(action)}
                disabled={action.disabled || action.busy}
                aria-label={action.ariaLabel || action.label}
              >
                <Icon className="friend-list-row__button-icon" aria-hidden="true" />
                <span className="friend-list-row__button-label">{label}</span>
              </Button>
            );
          })}
        </>
      )}
    />
  );
}

FriendListRow.propTypes = {
  userId: PropTypes.string,
  username: PropTypes.string,
  displayName: PropTypes.string,
  identityMetaLines: PropTypes.arrayOf(PropTypes.string),
  relationshipLabel: PropTypes.string,
  metaItems: PropTypes.arrayOf(PropTypes.string),
  summary: PropTypes.string,
  snapshotLines: PropTypes.arrayOf(PropTypes.string),
  snapshotFallback: PropTypes.string,
  connectionHeading: PropTypes.string,
  connectionValue: PropTypes.string,
  previewAriaLabel: PropTypes.string,
  avatarAlt: PropTypes.string,
  avatarUrl: PropTypes.string,
  publicMedia: PropTypes.bool,
  onPreview: PropTypes.func,
  actions: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string,
    label: PropTypes.string.isRequired,
    ariaLabel: PropTypes.string,
    busyLabel: PropTypes.string,
    variant: PropTypes.oneOf(["primary", "secondary", "success", "danger"]),
    disabled: PropTypes.bool,
    busy: PropTypes.bool,
    onClick: PropTypes.func,
  })),
  presenceStatus: PropTypes.shape({
    online: PropTypes.bool,
    lastSeen: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    placement: PropTypes.oneOf(["br", "bl", "tr", "tl"]),
    size: PropTypes.string,
    offset: PropTypes.string,
    border: PropTypes.string,
  }),
};
